import { useEffect } from "react";
import { Square, ArrowUp, Loader2, AudioLines } from "lucide-react";
import { useAudioRecording } from "@/hooks/useAudioRecording";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface DynamicVoiceSendButtonProps {
  hasText: boolean;
  disabled?: boolean;
  isTranscribing?: boolean;
  showVoice?: boolean;
  onSend: () => void;
  onRecordingComplete?: (blob: Blob) => void;
}

export const DynamicVoiceSendButton = ({
  hasText,
  disabled,
  isTranscribing,
  showVoice = true,
  onSend,
  onRecordingComplete,
}: DynamicVoiceSendButtonProps) => {
  const {
    recordingState,
    currentTime,
    audioLevel,
    audioBlob,
    permissionStatus,
    startRecording,
    stopRecording,
    resetRecording,
  } = useAudioRecording();

  const isRecording = recordingState === "recording";
  const isRequesting = recordingState === "requesting";

  // Auto-deliver blob when recording stops
  useEffect(() => {
    if (recordingState === "stopped" && audioBlob) {
      onRecordingComplete?.(audioBlob);
      resetRecording();
    }
  }, [recordingState, audioBlob]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleVoiceClick = async () => {
    if (disabled) return;
    try {
      await startRecording();
    } catch {
      if (permissionStatus === "denied") {
        toast.error("Microphone access denied. Please enable it in your browser settings.");
      }
    }
  };

  // Recording state → Stop button with audio level bars + timer
  if (isRecording) {
    return (
      <div className="flex items-center gap-2">
        {/* Audio level indicator */}
        <div className="flex items-center gap-0.5 h-4">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-0.5 rounded-full transition-all duration-75",
                audioLevel > i * 0.2 ? "bg-destructive" : "bg-muted-foreground/30"
              )}
              style={{
                height: `${Math.max(4, Math.min(16, 4 + audioLevel * 12 + (i % 2) * 2))}px`,
              }}
            />
          ))}
        </div>
        {/* Timer */}
        <span className="text-xs text-destructive font-medium tabular-nums">
          {formatTime(currentTime)}
        </span>
        {/* Stop button */}
        <button
          type="button"
          onClick={stopRecording}
          className="inline-flex items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 p-1.5"
          aria-label="Stop recording"
        >
          <Square className="h-4 w-4 fill-current" />
        </button>
      </div>
    );
  }

  // Transcribing state
  if (isTranscribing || isRequesting) {
    return (
      <button
        type="button"
        disabled
        className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground p-1.5"
        aria-label="Processing..."
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </button>
    );
  }

  // Has text → Send button
  if (hasText) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onSend}
        className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 p-1.5 disabled:cursor-not-allowed disabled:opacity-40"
        title="Send message"
        aria-label="Send message"
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    );
  }

  // Empty input + voice enabled → Voice button
  if (showVoice) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={handleVoiceClick}
        className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-9 min-w-9 space-x-1 p-2 px-3 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Start voice recording"
      >
        <AudioLines className="h-5 w-5" />
        <span className="hidden sm:inline text-sm">Voice</span>
      </button>
    );
  }

  // Empty input + no voice → Send button (disabled feel)
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSend}
      className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 p-1.5 disabled:cursor-not-allowed disabled:opacity-40"
      title="Send message"
      aria-label="Send message"
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
};
